import { useMemo, useState } from "react";
import { useTable, useFilters, useGlobalFilter, usePagination } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import "../../../assets/scss/_reactTable.scss";

const TableComponent = ({ columns, data, columnFilter }) => {
  const [filterInput, setFilterInput] = useState("");

  // Generate unique filter options for each column
  const columnFilterOptions = useMemo(() => {
    const options = {};
    columns.forEach((column) => {
      if (column.accessor && columnFilter.includes(column.accessor)) {
        const uniqueValues = [...new Set(data.map((row) => row[column.accessor]))];
        options[column.accessor] = uniqueValues;
      }
    });
    return options;
  }, [columns, data]);

  // Use the useTable hook with pagination
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    setFilter,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 20 }, // Default page size
    },
    useFilters,
    useGlobalFilter,
    usePagination
  );

  // Handle global search input change
  const handleSearchChange = (e) => {
    const value = e.target.value || "";
    setFilterInput(value);
    setGlobalFilter(value);
  };

  // Handle column filter dropdown change
  const handleColumnFilterChange = (columnId, value) => {
    setFilter(columnId, value === "all" ? undefined : value); // Reset filter if "all" is selected
  };

  // Export table data to Excel
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, "table_data.xlsx");
  };

  return (
    <div>
      <div className="row">
        {/* Global Search Input */}
        <div className="col-3">
          <input
            type="text"
            className="form-control table-search"
            style={{width: "100%", margin: "5px", padding: "10px"}}
            value={filterInput}
            onChange={handleSearchChange}
            placeholder="Search all columns..."
          />
        </div>

        {/* Column Filters */}
      
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column) =>
            column.canFilter && columnFilter.includes(column.id) ? (
              <div key={column.id} className="col-3">
                <select
                  key={column.id}
                  className="form-select"
                  defaultValue="all"
                  onChange={(e) => handleColumnFilterChange(column.id, e.target.value)}
                  style={{ margin: "5px", padding: "10px" }}
                >
                  <option value="all">All {column.Header}</option>
                  {columnFilterOptions[column.id]?.map((value, index) => (
                    <option key={index} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            ) : null
          )
        )}

        {/* Export Button */}
        <div className="col-3">
          <button className="table-button" onClick={exportToExcel} className="bookNow p-3" style={{ margin: "5px", padding: "10px" }}>Export to Excel</button>
        </div>
      </div>

      {/* Table */}
      <table className="table-react-com" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr className="table-tr" {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps()}>{column.render("Header")}</th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr className="table-tr" {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="pagination">
        <button className="table-button" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          {"<<"}
        </button>
        <button className="table-button" onClick={() => previousPage()} disabled={!canPreviousPage}>
          {"<"}
        </button>
        <span>
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <button className="table-button" onClick={() => nextPage()} disabled={!canNextPage}>
          {">"}
        </button>
        <button className="table-button" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          {">>"}
        </button>
        <select
          value={pageSize}
          className="form-select"
          style={{ width: "100px" }}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
          }}
        >
          {[5, 10, 20, 30, 40, 50].map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default TableComponent;